/**
 * Theme configuration for the admin interface
 *
 * Centralized theme system with:
 * - Design tokens for spacing, typography, layout
 * - Component overrides for MUI and React-Admin
 * - Reusable layout mixins
 * - Light and dark theme variants
 * - Environment-aware primary colors (production uses distinct color)
 *
 * Extracted from App.tsx for better organization and maintainability.
 */

import { defaultTheme } from 'react-admin';
import { deepmerge } from '@mui/utils';
import { muiOverrides } from './components/muiOverrides';
import { reactAdminOverrides } from './components/reactAdminOverrides';
import { isProduction, isLocal } from '../config/env';

// Re-export everything for easy imports
export * from './tokens';
export * from './mixins';

/**
 * Primary colors for environment-aware theming
 * - Local: Purple - Running with local Supabase (localhost)
 * - Dev: Blue - Hosted dev Supabase environment
 * - Production: Green - Production environment
 */
const PRIMARY_COLOR_LOCAL = '#ba63da'; // Purple
const PRIMARY_COLOR_DEV = '#4764ff'; // Blue
const PRIMARY_COLOR_PROD = '#458764'; // Green

/**
 * Get the primary color based on current environment
 */
function getPrimaryColor(): string {
  if (isProduction()) return PRIMARY_COLOR_PROD;
  if (isLocal()) return PRIMARY_COLOR_LOCAL;
  return PRIMARY_COLOR_DEV;
}

/**
 * Shared base theme configuration
 * Used as the foundation for both light and dark themes
 */
const sharedTheme = deepmerge(defaultTheme, {
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    allVariants: {
      letterSpacing: '-0.02em',
    },
  },
  components: {
    ...muiOverrides,
    ...reactAdminOverrides,
  },
});

/**
 * Light theme
 * Primary color changes based on environment (local=purple, dev=blue, prod=green)
 */
export const lightTheme = deepmerge(sharedTheme, {
  palette: {
    mode: 'light',
    primary: { main: getPrimaryColor() },
    secondary: { main: '#666666' },
    background: { default: '#f8f8f8', paper: '#ffffff' },
    text: { primary: '#333333', secondary: '#666666' },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: isProduction()
            ? '#ffffff'
            : `${theme.palette.primary.main}22`,
          color: '#000000',
          borderTop: `7px solid ${theme.palette.primary.main}`,
          borderBottom: '1px solid #e0e0e0',
          backdropFilter: 'blur(10px)',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#fafafa',
          borderRight: '1px solid #e0e0e0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
});

/**
 * Dark theme
 * Primary color changes based on environment (local=purple, dev=blue, prod=green)
 */
export const darkTheme = deepmerge(sharedTheme, {
  palette: {
    mode: 'dark',
    primary: { main: getPrimaryColor() },
    secondary: { main: '#aaaaaa' },
    background: { default: '#191b24', paper: '#1d2028ff' },
    text: { primary: '#e6e6e6ff', secondary: '#8f8f8fff' },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: isProduction()
            ? '#17191fff'
            : `${theme.palette.primary.main}22`,
          color: '#ffffff',
          borderTop: `7px solid ${theme.palette.primary.main}`,
          borderBottom: '1px solid #333333',
          backdropFilter: 'blur(10px)',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#17191fff',
          borderRight: '1px solid #333333',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: '#333333',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#333333',
        },
      },
    },
  },
});
